/**
 * pages/BrokerProfilePage.jsx
 * Route: /about-broker  (introducer menu → "About Your Broker")
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';
const tok = () => localStorage.getItem('token');

const levelLabel = l => l ? l.charAt(0).toUpperCase() + l.slice(1) : 'Broker';
const levelColor = l => l === 'owner' ? { color: '#065f46', bg: '#d1fae5' }
                       : l === 'manager' ? { color: '#1e40af', bg: '#dbeafe' }
                       : { color: '#374151', bg: '#f1f5f9' };

export default function BrokerProfilePage() {
  const navigate = useNavigate();
  const mobile = useMobile();
  const [profile, setProfile] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = tok();
    if (!t) { navigate('/login'); return; }
    let brokerCompanyId;
    try {
      const payload = JSON.parse(atob(t.split('.')[1]));
      brokerCompanyId = payload.brokerCompanyId;
    } catch { navigate('/login'); return; }
    if (!brokerCompanyId) {
      setError("Your account isn't linked to a broker company yet.");
      setLoading(false);
      return;
    }
    fetch(`${API}/company-profile/${brokerCompanyId}/broker-profile`, {
      headers: { Authorization: `Bearer ${t}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => { setProfile(data.company); setTeam(data.team || []); })
      .catch(() => setError('Failed to load broker profile.'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const ml = mobile ? 0 : 70;

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: ml }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🏛️</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Loading broker profile…</div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: ml, padding: 32 }}>
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '16px 20px', borderRadius: 12, fontSize: 14 }}>{error}</div>
      </div>
    </div>
  );

  if (!profile) return null;

  const owner   = team.find(m => m.broker_level === 'owner');
  const rest    = team.filter(m => m.broker_level !== 'owner');
  const ordered = [...(owner ? [owner] : []), ...rest];

  const isDirect = profile.lead_routing === 'direct';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: ml, minWidth: 0 }}>

        {/* ─── HERO BANNER ─── */}
        <div style={{
          background: 'linear-gradient(135deg, #0e1b2c 0%, #1a2f4e 55%, #0f766e 100%)',
          padding: mobile ? '28px 16px 40px' : '36px 40px 52px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -30, right: '25%', width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 99, padding: '4px 12px', marginBottom: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase' }}>Introducer Portal</span>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Broker Profile</span>
            </div>
            <h1 style={{ margin: 0, fontSize: mobile ? 24 : 30, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.15 }}>About Your Broker</h1>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>Your broker company, team, and how your referrals are handled</p>
          </div>
        </div>

        {/* ─── MAIN CONTENT ─── */}
        <div style={{ padding: mobile ? '20px 16px' : '32px 40px', maxWidth: 960 }}>

          {/* ─── COMPANY CARD ─── */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

            {/* Office photo (only if set) */}
            {profile.office_photo_url && (
              <div style={{ height: mobile ? 140 : 180, overflow: 'hidden' }}>
                <img src={profile.office_photo_url} alt="Office" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}

            {/* Logo + name row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: mobile ? '20px 20px 0' : '24px 28px 0' }}>
              <div style={{
                width: 60, height: 60, borderRadius: 12, overflow: 'hidden', flexShrink: 0,
                background: '#f8fafc', border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26,
              }}>
                {profile.logo_url
                  ? <img src={profile.logo_url} alt={profile.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : '🏛️'}
              </div>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ fontSize: mobile ? 17 : 20, fontWeight: 800, color: '#0e1b2c', margin: 0, letterSpacing: '-0.01em' }}>{profile.name}</h2>
                {profile.website && (
                  <a href={profile.website} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 13, color: '#10b981', textDecoration: 'none', fontWeight: 500, display: 'block' }}>
                    {profile.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
                {profile.phone && (
                  <a href={`tel:${profile.phone}`}
                    style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', fontWeight: 500, display: 'block', marginTop: 2 }}>
                    📞 {profile.phone}
                  </a>
                )}
              </div>
            </div>

            {/* Bio + routing badge */}
            <div style={{ padding: mobile ? '14px 20px 20px' : '16px 28px 24px' }}>
              {profile.company_bio ? (
                <p style={{ fontSize: 15, color: '#4b5a6e', lineHeight: 1.7, margin: '0 0 20px' }}>{profile.company_bio}</p>
              ) : (
                <p style={{ fontSize: 14, color: '#94a3b8', fontStyle: 'italic', margin: '0 0 20px' }}>No company description added yet.</p>
              )}

              {/* Lead routing badge */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                background: isDirect ? '#eff6ff' : '#fefce8',
                border: `1px solid ${isDirect ? '#bfdbfe' : '#fde68a'}`,
                borderRadius: 12, padding: '10px 16px', fontSize: 13, fontWeight: 600,
                color: isDirect ? '#1e40af' : '#92400e',
              }}>
                <span style={{ fontSize: 18 }}>{isDirect ? '🎯' : '📥'}</span>
                <div>
                  <div style={{ fontWeight: 700 }}>{isDirect ? 'Direct Assignment' : 'Shared Inbox'}</div>
                  <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 1 }}>
                    {isDirect
                      ? 'Your leads are assigned directly to your contact'
                      : 'Your leads go to the shared broker team inbox'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── TEAM SECTION ─── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0e1b2c', letterSpacing: '-0.01em' }}>Meet the team</h2>
            {team.length > 0 && (
              <span style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', padding: '4px 12px', borderRadius: 99, fontWeight: 600 }}>
                {team.length} member{team.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {team.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: '#fff', borderRadius: 16, border: '1px dashed #e2e8f0', color: '#94a3b8' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>No team members yet</div>
              <div style={{ fontSize: 13 }}>The broker team will appear here once members are added.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {ordered.map(member => {
                const lc = levelColor(member.broker_level);
                const isOwner = member.broker_level === 'owner';
                return (
                  <div key={member.id} style={{
                    background: '#fff',
                    border: `1px solid ${isOwner ? '#bbf7d0' : '#e2e8f0'}`,
                    borderRadius: 16,
                    overflow: 'hidden',
                    boxShadow: isOwner ? '0 2px 8px rgba(16,185,129,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
                  }}>
                    {/* Card header strip */}
                    <div style={{ height: 6, background: isOwner ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #94a3b8, #cbd5e1)' }} />

                    <div style={{ padding: '20px' }}>
                      {/* Avatar + name */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                        <div style={{
                          width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
                          background: isOwner ? 'linear-gradient(135deg, #d1fae5, #a7f3d0)' : '#f1f5f9',
                          border: `2px solid ${isOwner ? '#6ee7b7' : '#e2e8f0'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 22,
                        }}>
                          {member.profile_photo_url
                            ? <img src={member.profile_photo_url} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: 20, fontWeight: 700, color: isOwner ? '#065f46' : '#94a3b8' }}>{member.name?.charAt(0)?.toUpperCase()}</span>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#0e1b2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</div>
                          <div style={{
                            display: 'inline-block', marginTop: 4,
                            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                            color: lc.color, background: lc.bg,
                            borderRadius: 99, padding: '2px 10px',
                          }}>
                            {levelLabel(member.broker_level)}
                          </div>
                        </div>
                      </div>

                      {/* Bio */}
                      {member.bio
                        ? <p style={{ fontSize: 13, color: '#4b5a6e', lineHeight: 1.65, margin: '0 0 8px' }}>{member.bio}</p>
                        : <p style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', margin: '0 0 8px' }}>No bio added yet.</p>}
                      {(member.phone || member.phone_number) && (
                        <a href={`tel:${member.phone || member.phone_number}`}
                          style={{ fontSize: 12, color: '#64748b', textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          📞 {member.phone || member.phone_number}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
